import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { handleCallback } from '../lib/auth';

/**
 * /auth/callback?code=...&state=...
 *
 * Final step of the SPA's own OAuth round-trip. Reached only when our
 * frontend started the flow (`signIn()` in lib/auth.ts) — MCP clients
 * have their own redirect URIs and never land here.
 *
 * We exchange the code for an access + refresh token via /oauth/token,
 * stash them in sessionStorage, then navigate to whatever page kicked
 * off the sign-in (default: /manage).
 */
export default function AuthCallback() {
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { returnTo } = await handleCallback();
        if (cancelled) return;
        navigate(returnTo, { replace: true });
      } catch (err) {
        if (cancelled) return;
        setError((err as Error).message);
      }
    })();
    return () => { cancelled = true; };
  }, [navigate]);

  if (error) {
    return (
      <div className="max-w-xl mx-auto px-6 py-24 text-center">
        <div className="text-5xl mb-6">⚠️</div>
        <h1 className="text-2xl md:text-3xl font-bold">Sign-in failed</h1>
        <p className="mt-6 text-slate-300">{error}</p>
        <p className="mt-6 text-sm text-slate-400">
          Try again from <a className="text-amber-400 hover:text-amber-300" href="/manage">Manage</a>,
          or email{' '}
          <a className="text-amber-400 hover:text-amber-300" href="mailto:support@thedraftlion.com">
            support@thedraftlion.com
          </a>{' '}if it keeps failing.
        </p>
      </div>
    );
  }

  return (
    <div className="max-w-xl mx-auto px-6 py-24 text-center">
      <div className="text-5xl mb-6">🦁</div>
      <h1 className="text-2xl md:text-3xl font-bold">Finishing sign-in…</h1>
    </div>
  );
}
